import { useMemo, useState } from "react";
import { Skeleton } from "../../../../components/Skeleton/Skeleton.tsx";
import { trackEvent } from "../../../../tracking/tracking.ts";
import { NEW_BLANK_PROJECT_PRESET } from "../../../boardState/constants.ts";
import { useRedirectToBoard } from "../../../hooks/useRedirectToBoard.ts";
import { ViewLayout } from "../../ViewLayout.tsx";
import { useBoards } from "../hooks/useBoards.ts";
import { useCreateBoard } from "../hooks/useCreateBoard.ts";
import type { Board } from "../types.ts";
import { BoardTile } from "./BoardTile.tsx";

export const BoardsView = () => {
  const [searchText, setSearchText] = useState("");

  const redirectToBoard = useRedirectToBoard();

  const { userBoards, isLoading } = useBoards();
  const filteredBoards = useMemo(
    () =>
      userBoards?.filter((board: Board) =>
        board.name.toLowerCase().includes(searchText),
      ),
    [userBoards, searchText],
  );

  const { createBoard, isCreateBoardLoading } = useCreateBoard({
    onSuccess: (uuid) =>
      redirectToBoard({
        uuid,
        boardStatePreset: NEW_BLANK_PROJECT_PRESET,
      }),
  });

  const isEmpty = userBoards && userBoards.length === 0 && !isLoading;

  return (
    <ViewLayout
      title="Projects"
      searchTextProps={{
        searchText,
        setSearchText,
      }}
      creationButtonProps={{
        label: "New project",
        onClick: () => createBoard(),
        loading: isCreateBoardLoading,
      }}
      isEmpty={isEmpty}
      emptyStateProps={{
        iconName: "FolderOpen",
        title: "Projects",
        header: "Create your first project",
        description: "Projects help you keep your creations organized your way",
        buttonLabel: "New project",
      }}
    >
      <div className="w-full grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-400">
        {isLoading
          ? Array.from({ length: 8 }).map((_, index) => (
              // FIXME: The skeleton tile should be synchronized with the board tile size.
              <Skeleton key={index} className="h-[250px] w-full rounded-100" />
            ))
          : filteredBoards && filteredBoards.length > 0
          ? filteredBoards
              .sortDesc((board) => board.edited_at)
              .map((board) => (
                <BoardTile
                  key={board.uuid}
                  board={board}
                  onClick={() => {
                    trackEvent("project:open", {
                      project_uuid: board.uuid,
                    });
                    redirectToBoard({
                      uuid: board.uuid,
                      boardStatePreset: "blank",
                    });
                  }}
                />
              ))
          : null}
      </div>
    </ViewLayout>
  );
};
